/**
 * File download utility functions
 *
 * This file contains browser file download utilities for:
 * - Generic file downloads
 * - Video file downloads with format support
 * - Invoice PDF downloads with view/download modes
 */

import { baseAdminService } from '../../apis/baseService/baseAdminService';
import { baseService } from '../../apis/baseService/baseService';

const fileExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'zip', 'mov', 'mp4', 'jpeg', 'jpg', 'png'];

const getNameWithoutDots = (url: string, filename?: string): string =>
  ((filename || url.split('/').slice(-1)[0]?.split('?')[0]) ?? 'file')
    .split('.')
    .map((name) => (fileExtensions.includes(name) ? `.${name}` : name))
    .join('');

const simulatedUserInteraction = (link: HTMLAnchorElement): void => {
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

export const downloadFile = async (url: string, onError: () => void, filename?: string): Promise<boolean> => {
  if (!url) {
    return false;
  }

  try {
    const response = await fetch(url);

    if (!response.ok) {
      onError();
      return false;
    }

    const blob = await response.blob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = getNameWithoutDots(url, filename);
    simulatedUserInteraction(link);
    return true;
  } catch {
    onError();
    return false;
  }
};

export const downloadVideo = async (
  url: string,
  onError: () => void,
  fileName: string,
  format: string,
  isAdmin: boolean
): Promise<void> => {
  if (!url) return;

  const service = isAdmin ? baseAdminService : baseService;

  // Encode URL in case it has any special characters
  url = encodeURI(url);

  await service
    .get(url, { responseType: 'blob' })
    .then(({ data }) => {
      const newBlob = new Blob([data], { type: `video/${format}` });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(newBlob);

      // Decode filename in case it has any special characters
      const nameForFile = decodeURIComponent(fileName.replace(/\./g, ''));
      link.download = format === 'mov' ? `${nameForFile}.mov` : nameForFile;
      simulatedUserInteraction(link);
    })
    .catch(() => {
      onError();
    });
};

export const downloadInvoice = async (
  url: string,
  onError: () => void,
  fileName: string,
  isView: boolean,
  isAdmin: boolean
): Promise<void> => {
  if (!url) return;

  const service = isAdmin ? baseAdminService : baseService;
  await service
    .get(url, { responseType: 'blob' })
    .then(({ data }) => {
      const newBlob = new Blob([data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.target = '_blank';
      link.href = URL.createObjectURL(newBlob);
      if (!isView) link.download = fileName;
      simulatedUserInteraction(link);
    })
    .catch(() => {
      onError();
    });
};
